import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { 
  Package, CreditCard, CheckCircle, ArrowLeft, Sparkles,
  Calendar, Users, User, Wallet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { PackageInfo, packagePriceFor, formatEuro } from "@/components/PackageInfo";
import { AboHint, AboPlanner, AboDates, perWeekOf } from "@/components/abo/AboOrder";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRAINING_ICONS = {
  einzeltraining_kind: User,
  einzeltraining_erwachsen: User,
  gruppentraining_2_4: Users,
  gruppentraining_5_plus: Users,
  torwarttraining: User,
};

export default function PaketePage() {
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [purchaseSuccess, setPurchaseSuccess] = useState(null);
  const [aboPlan, setAboPlan] = useState(null);
  
  const [formData, setFormData] = useState({
    packageType: "",
    trainingType: "",
    customerName: "",
    customerEmail: "",
    customerPhone: "",
    paymentMethod: "paypal",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/appointments/training-types`);
      const data = await res.json();
      setTrainingTypes(data.training_types || []);
      setPackages(data.packages || []);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  };

  const calculatePrice = () => {
    const pkg = packages.find(p => p.id === formData.packageType);
    return packagePriceFor(pkg, formData.trainingType)?.price || 0;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/appointments/packages/purchase`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_type: formData.packageType,
          training_type: formData.trainingType,
          customer: {
            name: formData.customerName,
            email: formData.customerEmail,
            phone: formData.customerPhone,
          },
          payment_method: formData.paymentMethod,
          ...(aboPlan ? { abo_month: aboPlan.month, abo_slots: aboPlan.slots } : {}),
        }),
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        setPurchaseSuccess(data);
        setStep(3);
        toast.success("Paket erfolgreich bestellt!");
      } else {
        toast.error(data.detail || "Bestellung fehlgeschlagen");
      }
    } catch (err) {
      toast.error("Ein Fehler ist aufgetreten");
    }
    setLoading(false);
  };

  const selectedPackage = packages.find(p => p.id === formData.packageType);
  const selectedTraining = trainingTypes.find(t => t.id === formData.trainingType);
  const totalPrice = calculatePrice();

  return (
    <>
      <Helmet>
        <title>Sparpakete kaufen | Aktas Personal Fussballtraining</title>
        <meta name="description" content="Kaufe 5er oder 10er Karten und spare bis zu 10% auf dein Personal Training." />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white hover:text-[#c1ff72] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Zurück</span>
            </Link>
            <h1 className="font-['Bebas_Neue'] text-xl sm:text-2xl tracking-wide">
              SPAR<span className="text-[#c1ff72]">PAKETE</span>
            </h1>
            <Link to="/buchen" className="text-[#c1ff72] text-sm hover:underline">
              Einzelbuchung
            </Link>
          </div>
        </header>

        {/* Main Content */}
        <main className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          
          {/* Step 1: Package & Training Selection */}
          {step === 1 && (
            <div className="space-y-8" data-testid="step-package-select">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 bg-[#c1ff72]/10 text-[#c1ff72] px-4 py-2 rounded-full text-sm mb-4">
                  <Sparkles className="w-4 h-4" />
                  <span>Spare bis zu 10%</span>
                </div>
                <h2 className="font-['Bebas_Neue'] text-3xl sm:text-5xl mb-2">
                  WÄHLE DEIN <span className="text-[#c1ff72]">SPARPAKET</span>
                </h2>
                <p className="text-[#A1A1AA]">Kaufe mehrere Einheiten im Voraus und spare!</p>
              </div>

              {/* Package Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                {packages.map((pkg) => (
                  <button
                    key={pkg.id}
                    onClick={() => setFormData(prev => ({ ...prev, packageType: pkg.id }))}
                    className={`p-6 rounded-xl border text-left transition-all relative overflow-hidden ${
                      formData.packageType === pkg.id
                        ? "bg-[#c1ff72]/10 border-[#c1ff72]"
                        : "bg-[#1A1A1A] border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`package-${pkg.id}`}
                  >
                    {pkg.id === "10er_karte" && (
                      <div className="absolute top-3 right-3 bg-[#c1ff72] text-[#0A0A0A] text-xs font-bold px-2 py-1 rounded">
                        BELIEBT
                      </div>
                    )}
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        formData.packageType === pkg.id ? "bg-[#c1ff72]/20" : "bg-white/5"
                      }`}>
                        <Package className={`w-5 h-5 ${formData.packageType === pkg.id ? "text-[#c1ff72]" : "text-white"}`} />
                      </div>
                      <h3 className="font-['Bebas_Neue'] text-2xl">{pkg.name}</h3>
                    </div>
                    <PackageInfo pkg={pkg} />
                    {pkg.monthly && <div className="mt-3"><AboHint perWeek={perWeekOf(pkg.id)} testId={`abo-hint-card-${pkg.id}`} /></div>}
                  </button>
                ))}
              </div>

              {/* Training Type Selection */}
              {formData.packageType && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Für welches Training?</h3>
                  <div className="grid gap-3">
                    {trainingTypes.filter(t => packagePriceFor(selectedPackage, t.id)).map((training) => {
                      const Icon = TRAINING_ICONS[training.id] || User;
                      return (
                        <button
                          key={training.id}
                          onClick={() => setFormData(prev => ({ ...prev, trainingType: training.id }))}
                          className={`p-4 rounded-lg border flex items-center justify-between transition-all ${
                            formData.trainingType === training.id
                              ? "bg-[#c1ff72]/10 border-[#c1ff72]"
                              : "bg-[#1A1A1A] border-white/10 hover:border-white/30"
                          }`}
                          data-testid={`training-${training.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 ${formData.trainingType === training.id ? "text-[#c1ff72]" : "text-white"}`} />
                            <span>{training.name}</span>
                          </div>
                          <span className="text-[#c1ff72] font-semibold">
                            {packagePriceFor(selectedPackage, training.id) && formatEuro(packagePriceFor(selectedPackage, training.id).price)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {selectedPackage?.monthly && formData.trainingType && (
                <AboPlanner packageType={formData.packageType} trainingType={formData.trainingType} onChange={setAboPlan} />
              )}

              {/* Price Preview */}
              {formData.packageType && formData.trainingType && (
                <div className="bg-[#1A1A1A] rounded-xl p-6 border border-[#c1ff72]/30">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-[#71717A]">{selectedPackage?.name} • {selectedTraining?.name}</p>
                      <p className="text-sm text-[#71717A]">{selectedPackage?.credits} Einheiten</p>
                    </div>
                    <div className="text-right">
                      {packagePriceFor(selectedPackage, formData.trainingType)?.regular_price > totalPrice && (
                        <p className="text-[#71717A] text-sm line-through">
                          {formatEuro(packagePriceFor(selectedPackage, formData.trainingType).regular_price)}
                        </p>
                      )}
                      <p className="font-['Bebas_Neue'] text-4xl text-[#c1ff72]" data-testid="package-total-price">
                        {formatEuro(totalPrice)}{selectedPackage?.monthly && <span className="text-sm text-[#71717A] font-normal"> / Monat</span>}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <Button
                onClick={() => setStep(2)}
                disabled={!formData.packageType || !formData.trainingType || (selectedPackage?.monthly && !aboPlan)}
                className="w-full bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold py-6"
                data-testid="btn-next-step1"
              >
                Weiter zur Bestellung
              </Button>
            </div>
          )}

          {/* Step 2: Customer Data & Payment */}
          {step === 2 && (
            <div className="space-y-6" data-testid="step-customer-payment">
              <div className="text-center mb-8">
                <h2 className="font-['Bebas_Neue'] text-3xl sm:text-4xl mb-2">
                  DEINE <span className="text-[#c1ff72]">BESTELLUNG</span>
                </h2>
              </div>

              {/* Summary */}
              <div className="bg-[#1A1A1A] rounded-lg p-4 border border-white/10">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold">{selectedPackage?.name}</p>
                    <p className="text-sm text-[#71717A]">{selectedPackage?.credits}x {selectedTraining?.name}</p>
                  </div>
                  <p className="font-['Bebas_Neue'] text-2xl text-[#c1ff72]">{formatEuro(totalPrice)}{selectedPackage?.monthly && " / Monat"}</p>
                </div>
                {aboPlan && <div className="mt-4 space-y-3 border-t border-white/10 pt-4" data-testid="abo-order-summary">
                  <p className="text-sm">Feste Trainingszeit: <b>{aboPlan.preview.label} Uhr</b> · Start: <b>{aboPlan.preview.month_label}</b></p>
                  <AboDates preview={aboPlan.preview} testId="abo-order-dates" />
                  <AboHint perWeek={perWeekOf(formData.packageType)} testId="abo-hint-order" />
                  <p className="text-xs text-[#71717A]">Die Termine werden mit der Bestellung für dich vorgemerkt und nach Zahlungseingang verbindlich bestätigt.</p>
                </div>}
              </div>

              {/* Customer Form */}
              <div className="bg-[#1A1A1A] rounded-lg p-4 sm:p-6 border border-white/10 space-y-4">
                <h3 className="font-semibold">Deine Daten</h3>
                <div>
                  <Label htmlFor="name" className="text-white">Name *</Label>
                  <Input
                    id="name"
                    value={formData.customerName}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                    placeholder="Max Mustermann"
                    className="mt-1 bg-[#0A0A0A] border-white/20"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-white">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.customerEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
                    placeholder="max@beispiel.de"
                    className="mt-1 bg-[#0A0A0A] border-white/20"
                    data-testid="input-email"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-white">Telefon *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.customerPhone}
                    onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                    placeholder="0174 1234567"
                    className="mt-1 bg-[#0A0A0A] border-white/20"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div className="space-y-3">
                <Label className="text-white">Zahlungsart</Label>
                <RadioGroup 
                  value={formData.paymentMethod} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, paymentMethod: v }))}
                  className="space-y-3"
                >
                  <div className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    formData.paymentMethod === "paypal" ? "bg-[#0070ba]/10 border-[#0070ba]" : "bg-[#1A1A1A] border-white/10"
                  }`}>
                    <RadioGroupItem value="paypal" id="paypal" />
                    <Label htmlFor="paypal" className="flex-1 cursor-pointer">
                      <span className="font-semibold">PayPal</span>
                      <p className="text-sm text-[#71717A]">Sofort online bezahlen</p>
                    </Label>
                  </div>
                  
                  <div className={`flex items-center space-x-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    formData.paymentMethod === "bank_transfer" ? "bg-[#c1ff72]/10 border-[#c1ff72]" : "bg-[#1A1A1A] border-white/10"
                  }`}>
                    <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                    <Label htmlFor="bank_transfer" className="flex-1 cursor-pointer">
                      <span className="font-semibold">Banküberweisung</span>
                      <p className="text-sm text-[#71717A]">Per Überweisung bezahlen</p>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                  Zurück
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={loading || !formData.customerName || !formData.customerEmail || !formData.customerPhone}
                  className="flex-1 bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c] font-semibold"
                  data-testid="btn-submit"
                >
                  {loading ? "Wird bestellt..." : `Jetzt ${formatEuro(totalPrice)} bestellen`}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && purchaseSuccess && (
            <div className="text-center space-y-6" data-testid="step-success">
              <div className="w-20 h-20 bg-[#c1ff72]/10 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-[#c1ff72]" />
              </div>
              
              <div>
                <h2 className="font-['Bebas_Neue'] text-3xl sm:text-4xl mb-2">
                  BESTELLUNG <span className="text-[#c1ff72]">ERFOLGREICH!</span>
                </h2>
                <p className="text-[#A1A1AA]">
                  Dein Paket wird nach Zahlungseingang aktiviert.
                </p>
              </div>

              <div className="bg-[#1A1A1A] rounded-lg p-6 border border-white/10 text-left">
                <div className="flex items-center gap-2 mb-4">
                  <Wallet className="w-5 h-5 text-[#c1ff72]" />
                  <h3 className="font-semibold">Paketdetails</h3>
                </div>
                <div className="space-y-2 text-sm">
                  <p><span className="text-[#71717A]">Paket-ID:</span> <span className="font-mono text-[#c1ff72]">{purchaseSuccess.package_id}</span></p>
                  <p><span className="text-[#71717A]">Paket:</span> {purchaseSuccess.package?.name}</p>
                  <p><span className="text-[#71717A]">Training:</span> {purchaseSuccess.package?.training_type}</p>
                  <p><span className="text-[#71717A]">Einheiten:</span> {purchaseSuccess.package?.credits}</p>
                  <p><span className="text-[#71717A]">Preis:</span> <span className="text-[#c1ff72] font-semibold">{purchaseSuccess.package?.total_price}€</span></p>
                </div>
                {purchaseSuccess.abo && <div className="mt-4 space-y-3" data-testid="abo-success-details">
                  <p className="text-sm">Feste Trainingszeit: <b>{purchaseSuccess.abo.label} Uhr</b></p>
                  <AboDates preview={purchaseSuccess.abo} testId="abo-success-dates" />
                  <p className="text-sm text-yellow-300">Status: vorgemerkt – die Termine werden nach Zahlungseingang verbindlich bestätigt.</p>
                  <AboHint perWeek={perWeekOf(formData.packageType)} testId="abo-hint-success" />
                </div>}
              </div>

              {/* Payment Info */}
              {purchaseSuccess.payment_link && (
                <div className="bg-[#0070ba]/10 border border-[#0070ba]/30 rounded-lg p-4">
                  <p className="font-semibold text-[#0070ba] mb-2">PayPal Zahlung</p>
                  <a 
                    href={purchaseSuccess.payment_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block bg-[#0070ba] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#005ea6] transition-colors"
                  >
                    Jetzt {purchaseSuccess.package?.total_price}€ bezahlen
                  </a>
                </div>
              )}

              {purchaseSuccess.bank_details && (
                <div className="bg-[#1A1A1A] border border-[#c1ff72]/30 rounded-lg p-4 text-left">
                  <p className="font-semibold text-[#c1ff72] mb-3">Banküberweisung</p>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-[#71717A]">Empfänger:</span> {purchaseSuccess.bank_details.recipient}</p>
                    <p><span className="text-[#71717A]">IBAN:</span> <span className="font-mono">{purchaseSuccess.bank_details.iban}</span></p>
                    <p><span className="text-[#71717A]">BIC:</span> {purchaseSuccess.bank_details.bic}</p>
                    <p><span className="text-[#71717A]">Bank:</span> {purchaseSuccess.bank_details.bank}</p>
                    <p><span className="text-[#71717A]">Verwendungszweck:</span> <span className="text-[#c1ff72] font-mono">{purchaseSuccess.bank_details.reference}</span></p>
                    <p><span className="text-[#71717A]">Betrag:</span> <span className="text-[#c1ff72] font-semibold">{purchaseSuccess.bank_details.amount}</span></p>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/" className="flex-1">
                  <Button variant="outline" className="w-full">
                    Zur Startseite
                  </Button>
                </Link>
                <Link to="/buchen" className="flex-1">
                  <Button className="w-full bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c]">
                    Termin buchen
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
